import produce from "immer";
import { useTranslation } from "react-i18next";
import {
  Box,
  FormControlLabel,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import VariablesSelector from "components/VariablesSelector";
import { VerticalLine, OptionGroup } from "components/Styled";
import { PredicateTitle, StyledTitle } from "../Styled";
import { SecurityDefinition } from "@dashboard-v3/api";
import { FormState } from "../types";
import SelectDefaultDef from "./SelectSecurityDef";

type PredicateField = "variableIdList" | "definitionId";

interface Props {
  formData: {
    formState: FormState;
    setValues: (values: Partial<FormState>) => void;
  };
  index: number;
  definitions: SecurityDefinition[];
  errors?: { variableIdList?: string; definitionId?: string };
  touched?: { variableIdList?: boolean; definitionId?: boolean };
  onBlur: (field: PredicateField) => void;
  onRemove: (index: number) => void;
}

enum MatchOption {
  AllMatch = "ALL_MATCH",
  OneMatches = "ONE_MATCHES",
  NoMatches = "NO_MATCHES",
}

export default function Predicates({
  formData,
  index,
  definitions,
  errors,
  touched,
  onBlur,
  onRemove,
}: Props) {
  const { t } = useTranslation("securityPolicies");
  const { formState, setValues } = formData;
  const { predicates } = formState;
  const { variableIdList, definitionId } = formState.predicates[index];

  const variableIdError = touched?.variableIdList && errors?.variableIdList;
  const definitionIdError = touched?.definitionId && errors?.definitionId;

  const handleVariableIds = (variableIds: string[]) => {
    const newState = produce(formState, draft => {
      draft.predicates[index].variableIdList = variableIds;
    });

    setValues(newState);
  };

  const handleMatchChange = event => {
    const value = event.target.value as MatchOption;
    const newState = produce(formState, draft => {
      if (value === MatchOption.AllMatch) {
        draft.predicates[index].firstMatch = false;
        draft.predicates[index].match = true;
      }

      if (value === MatchOption.NoMatches) {
        draft.predicates[index].firstMatch = false;
        draft.predicates[index].match = false;
      }

      if (value === MatchOption.OneMatches) {
        draft.predicates[index].firstMatch = true;
        draft.predicates[index].match = true;
      }
    });

    setValues(newState);
  };

  const handleDefinitionId = e => {
    const { name, value } = e.target;
    const updated = predicates.map((item, i) => {
      let update = value;
      if (value === "DO_NOT_UPLOAD") update = null;
      return i === index ? { ...item, [name]: update } : item;
    });
    setValues({ predicates: updated });
  };

  const getMatchValue = () => {
    const { match, firstMatch } = predicates[index];
    if (match && !firstMatch) return MatchOption.AllMatch;
    if (match && firstMatch) return MatchOption.OneMatches;
    if (!match && !firstMatch) return MatchOption.NoMatches;
    return MatchOption.AllMatch;
  };

  return (
    <>
      <VerticalLine size="sm" />
      <Paper
        style={{ padding: "24px 24px 32px 24px" }}
        data-testid={`sp-condition-block-${index}`}
      >
        <PredicateTitle color="textSecondary">
          {t("form.predicates.title", { number: index + 1 })}
          {predicates.length > 1 && (
            <IconButton
              onClick={() => onRemove(index)}
              size="small"
              aria-label="delete"
              data-testid={`sp-predicate-delete-${index}`}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </PredicateTitle>
        <OptionGroup>
          <StyledTitle>{t("form.predicates.variableId.title")}</StyledTitle>
          <VariablesSelector
            label={t("form.predicates.variableId.label")}
            value={variableIdList}
            onChange={handleVariableIds}
            onBlur={() => onBlur("variableIdList")}
            error={Boolean(variableIdError)}
            helperText={variableIdError || ""}
            data-testid={`sp-predicate-variables-${index}`}
          />
          <Box display="flex" mt=".5em" mb="1em">
            <RadioGroup
              row
              aria-label="match-or-not"
              name={`match-or-not-${index}`}
              value={getMatchValue()}
              onChange={handleMatchChange}
              data-testid={`sp-predicate-match-${index}`}
            >
              <FormControlLabel
                value={MatchOption.AllMatch}
                control={<Radio color="primary" />}
                label={t("form.allMatch")}
              />
              <FormControlLabel
                value={MatchOption.OneMatches}
                control={<Radio color="primary" />}
                label={t("form.oneMatches")}
              />
              <FormControlLabel
                value={MatchOption.NoMatches}
                control={<Radio color="primary" />}
                label={t("form.noMatches")}
              />
            </RadioGroup>
          </Box>
        </OptionGroup>
        <OptionGroup>
          <StyledTitle>{t("form.predicates.definitionId.title")}</StyledTitle>
          <SelectDefaultDef
            options={definitions}
            value={definitionId}
            name="definitionId"
            onChange={handleDefinitionId}
            onBlur={() => onBlur("definitionId")}
            error={Boolean(definitionIdError)}
            helperText={definitionIdError || ""}
            data-testid={`sp-predicate-definition-${index}`}
          />
        </OptionGroup>
      </Paper>
    </>
  );
}
